import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import SEOHead from '../components/SEOHead'
import GallerySection from '../components/GallerySection'
import { useCmsPage } from '../hooks/useCmsPage'

export default function HomePage() {
  const { page } = useCmsPage('index')
  const videoRef = useRef(null)

  const title = page?.meta_title || 'Lej en DJ - CasperDJ '
  const description =
    page?.meta_description ||
    'Planlægger du at leje en DJ til din næste fest? CasperDJ kører ud til både private fester, bryllupper og firmaevents. Med professionelt udstyr og en bred musikalsk palette skaber jeg uforglemmelige oplevelser!'

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {})
    }
  }, [])

  return (
    <div className="bg-neutral-950 text-white min-h-screen">
      <SEOHead title={title} description={description} />

      {/* Hero Section */}
      <section className="relative isolate overflow-hidden min-h-[85vh] md:min-h-screen flex items-center justify-center pt-48 pb-28 sm:pt-56 sm:pb-32 md:pt-64 md:pb-36">
        {/* Background Video */}
        <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            poster="https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/casperdjballet.jpg"
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source
              src="https://assets.auxodev.dk/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/casperdjballet.mp4"
              type="video/mp4"
            />
          </video>
          {/* Contrast overlay */}
          <div className="absolute inset-0 bg-black/25 z-4" />
        </div>

        {/* Top subtle fade for header */}
        <div className="absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/40 via-black/15 to-transparent pointer-events-none z-10" />

        {/* Seamless smooth gradient transition directly into section below (bg-neutral-950) */}
        <div className="absolute inset-x-0 bottom-0 h-36 sm:h-48 md:h-56 bg-gradient-to-t from-neutral-950 via-neutral-950/40 to-transparent pointer-events-none z-10" />

        {/* Ambient subtle glow */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] bg-indigo-500/10 blur-[150px] rounded-full pointer-events-none z-10" />

        {/* Center Content Block */}
        <div className="max-w-3xl mx-auto flex flex-col items-center relative z-20 px-4 text-center drop-shadow-[0_2px_12px_rgba(0,0,0,0.8)]">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight text-white mb-5">
            CasperDJ
          </h1>

          <p className="text-sm sm:text-base md:text-lg text-neutral-200 leading-relaxed font-normal max-w-2xl mx-auto">
            I årevis har jeg lagt lyd til{' '}
            <Link
              to="/firmafest"
              className="text-white underline underline-offset-4 decoration-1 hover:text-neutral-100 transition-colors"
            >
              firmafester
            </Link>
            , konfirmationer, intime fejringer, storslåede{' '}
            <Link
              to="/bryllup"
              className="text-white underline underline-offset-4 decoration-1 hover:text-neutral-100 transition-colors"
            >
              bryllupper
            </Link>
            ,{' '}
            <Link
              to="/privatfest"
              className="text-white underline underline-offset-4 decoration-1 hover:text-neutral-100 transition-colors"
            >
              privatfester
            </Link>
            , som runde fødselsdage og diverse events i Odense og resten af Danmark.
          </p>

            {/* Primary CTA Button */}
          <Link
            to="/foresprgsel"
            className="mt-8 bg-white text-black font-extrabold uppercase tracking-widest text-xs sm:text-sm px-10 sm:px-12 py-3.5 sm:py-4 hover:bg-neutral-200 transition-all duration-200 shadow-2xl hover:shadow-white/20 active:scale-95 cursor-pointer"
          >
            Forespørgsel
          </Link>

          {/* Trust Highlights Strip */}
          <div className="mt-12 pt-8 border-t border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-8 w-full max-w-2xl text-center">
            <div className="flex flex-col items-center">
              <span className="text-white font-bold text-base sm:text-lg tracking-tight">500+ Fester</span>
              <span className="text-neutral-400 text-xs mt-0.5">Erfaring fra hele landet</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-white font-bold text-base sm:text-lg tracking-tight">100% Skræddersyet</span>
              <span className="text-neutral-400 text-xs mt-0.5">Musikken tilpasses dine gæster</span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-white font-bold text-base sm:text-lg tracking-tight">Pioneer Lyd &amp; Lys</span>
              <span className="text-neutral-400 text-xs mt-0.5">Professionelt mobildiskotek</span>
            </div>
          </div>
        </div>
      </section>

      {/* Section: HVORFOR VÆLGE CASPERDJ? */}
      <section className="py-20 md:py-28 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative aspect-[3/4] rounded-3xl overflow-hidden border border-neutral-800 shadow-2xl bg-neutral-900">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/image-asset_9cec8607.jpeg"
                  alt="Casper DJ mixer musik"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="lg:col-span-7 order-1 lg:order-2 space-y-6">
              <span className="text-xs font-semibold tracking-widest text-gold-400 uppercase">
                Erfaring &amp; Tryghed
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                HVORFOR VÆLGE CASPERDJ?
              </h2>
              <p className="text-base sm:text-lg text-neutral-300 leading-relaxed">
                Når du lejer en DJ sikrer du ikke kun musikken – du sørger for en unik stemningsoplevelse! CasperDJ har den erfaring der skal til for at skabe en helt særlig og unik atmosfære til festen. Jeg tager hånd om enhver detalje, fra de første toner til den sidste dans. Mit mål er altid at levere en mindeværdig oplevelse for dig og dine gæster.
              </p>
              <div className="pt-2">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-gold-400 text-neutral-950 font-bold text-sm uppercase tracking-wider hover:bg-gold-300 transition-colors shadow-md cursor-pointer"
                >
                  <span>Kontakt mig</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3 Categories / Fest Typer */}
      <section className="py-20 md:py-28 border-y border-neutral-900" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-semibold tracking-widest text-gold-400 uppercase">
              Skræddersyede løsninger
            </span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight mt-1">
              Fest typer
            </h2>
            <p className="text-neutral-400 text-sm sm:text-base mt-2">
              Uanset anledningen sammensætter CasperDJ det perfekte musik- og lydsetup.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Firmafest */}
            <Link
              to="/firmafest"
              className="group rounded-3xl bg-neutral-950 border border-neutral-800/90 overflow-hidden hover:border-gold-400/50 transition-all duration-300 hover:-translate-y-1 shadow-xl flex flex-col cursor-pointer"
            >
              <div className="aspect-square w-full overflow-hidden bg-neutral-900">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/firmafest.png"
                  alt="DJ til Firmafest"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col justify-between text-center items-center">
                <div>
                  <p className="text-neutral-400 text-sm mt-2 leading-relaxed">
                    Professionel musikafvikling til receptioner, sommerfester og julefrokoster.
                  </p>
                </div>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-gold-400 tracking-wider uppercase">
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </span>
              </div>
            </Link>

            {/* Privatfest */}
            <Link
              to="/privatfest"
              className="group rounded-3xl bg-neutral-950 border border-neutral-800/90 overflow-hidden hover:border-gold-400/50 transition-all duration-300 hover:-translate-y-1 shadow-xl flex flex-col cursor-pointer"
            >
              <div className="aspect-square w-full overflow-hidden bg-neutral-900">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/privatfest.png"
                  alt="DJ til Privatfest"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col justify-between text-center items-center">
                <div>
                  <p className="text-neutral-400 text-sm mt-2 leading-relaxed">
                    Fødselsdage, konfirmationer og havefester med den helt rigtige playliste.
                  </p>
                </div>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-gold-400 tracking-wider uppercase">
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </span>
              </div>
            </Link>

            {/* Bryllup */}
            <Link
              to="/bryllup"
              className="group rounded-3xl bg-neutral-950 border border-neutral-800/90 overflow-hidden hover:border-gold-400/50 transition-all duration-300 hover:-translate-y-1 shadow-xl flex flex-col cursor-pointer"
            >
              <div className="aspect-square w-full overflow-hidden bg-neutral-900">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/bryllup.png"
                  alt="DJ til Bryllup"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-6 flex-1 flex flex-col justify-between text-center items-center">
                <div>
                  <p className="text-neutral-400 text-sm mt-2 leading-relaxed">
                    En uforglemmelig bryllupsaften med skræddersyet musik fra middag til brudevals.
                  </p>
                </div>
                <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-gold-400 tracking-wider uppercase">
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right text-[10px] group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Section: Lej CasperDJ til din begivenhed */}
      <section className="py-20 md:py-28 bg-neutral-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7 space-y-6">
              <span className="text-xs font-semibold tracking-widest text-gold-400 uppercase">
                Musik med passion
              </span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight">
                Lej CasperDJ til din begivenhed
              </h2>
              <p className="text-base sm:text-lg text-neutral-300 leading-relaxed">
                Min passion for at skabe musikoplevelser strækker sig til enhver form for begivenhed, uanset om det er en firmafest, privatfest, eller et bryllup. CasperDJ er her for at opfylde dine musikalske ønsker og omsætte dem til virkelighed.
              </p>

              <div className="pt-2">
                <h3 className="text-sm font-bold uppercase tracking-wider text-gold-400 mb-4">
                  Vores tilbud:
                </h3>
                <ul className="space-y-4 text-sm sm:text-base text-neutral-300">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-gold-400/10 border border-gold-400/30 text-gold-400 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                    </span>
                    <div>
                      <Link to="/privatfest" className="font-bold text-white hover:text-gold-400 underline decoration-neutral-700 underline-offset-4">
                        Private Fester:
                      </Link>{' '}
                      Gør dine private fester ekstraordinære med den rette musik. Fra intime sammenkomster til store fejringer har jeg den musik, der skaber den perfekte stemning.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-gold-400/10 border border-gold-400/30 text-gold-400 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                    </span>
                    <div>
                      <Link to="/bryllup" className="font-bold text-white hover:text-gold-400 underline decoration-neutral-700 underline-offset-4">
                        Bryllupper:
                      </Link>{' '}
                      Et bryllup er én af livets største begivenheder, og det kan ikke gøres om. Som din DJ vil jeg gerne bidrage til en uforglemmelig aften, og jeg vil sørge for, at hvert eneste øjeblik får den rette musikalske ledsagelse.
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded-full bg-gold-400/10 border border-gold-400/30 text-gold-400 flex items-center justify-center shrink-0 mt-0.5">
                      <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                    </span>
                    <div>
                      <Link to="/firmafest" className="font-bold text-white hover:text-gold-400 underline decoration-neutral-700 underline-offset-4">
                        Firmafester:
                      </Link>{' '}
                      En god firmafest er en positiv samlende oplevelse, og som dj til firmafesten, så sikrer CasperDJ at festen bliver, som I ønsker den
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative aspect-[4/5] rounded-3xl overflow-hidden border border-neutral-800 shadow-2xl bg-neutral-900">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/bce4b86c-87d9-405d-a99b-f30b07aaa8a2/casperdj0184-ricofeldfoss-1.jpg"
                  alt="Casper DJ arrangement"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CMS Gallery Section */}
      <GallerySection
        gallerySlug="galleri-billeder"
        title="Galleri fra fester og events"
        subtitle="Se billeder fra dansegulvet, lyd- og lysopsætning ved firmafester, privatfester og bryllupper."
      />
    </div>
  )
}
